import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  useCurrentStateAsRef,
  useWriteContext,
} from "../../vendor/src/features/remotion-editor/editor/utils/use-context";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import { applyTimingInput } from "./timing-input";
import { ownsEdit, type EditOwnership } from "./edit-ownership";

type Field = "start" | "duration";
type Session = EditOwnership & {
  field: Field;
  focused: boolean;
  cancelled: boolean;
  warning: string | null;
  ids: readonly string[];
};
const owns = ownsEdit;

/** One typing/scrub gesture is one undo entry; every preview derives from its original document. */
export function useTimingInput(itemId: string, targetIds: readonly string[]) {
  const stateRef = useCurrentStateAsRef(),
    { setState } = useWriteContext();
  const active = useRef<Session | null>(null);
  const [revision, setRevision] = useState({ start: 0, duration: 0 });
  const finish = useCallback(
    (cancel = false, unmount = false) => {
      const session = active.current;
      if (!session) return;
      active.current = null;
      cancel = cancel || session.cancelled;
      const conflict = !owns(session, stateRef.current);
      setState({
        mutationOwner: session.token,
        update: (current) => {
          const pendingOwner =
            current.timelineInputOwner === session.token
              ? null
              : current.timelineInputOwner;
          if (!cancel || !owns(session, current))
            return { ...current, timelineInputOwner: pendingOwner };
          return {
            ...current,
            undoableState: session.base.undoableState,
            selectedItems: session.base.selectedItems,
            timelineTimeEditApplied: true,
            timelineInputOwner: pendingOwner,
          };
        },
        commitToUndoStack: !cancel,
      });
      if (!unmount) {
        // Refresh only the completed field. Remounting both would swallow Tab focus into its sibling.
        setRevision((value) => ({
          ...value,
          [session.field]: value[session.field] + 1,
        }));
        if (conflict)
          toast.info(
            "다른 편집이 적용되어 시간 조정을 멈췄습니다. 현재 상태에서 다시 조정해 주세요.",
          );
        else if (!cancel && session.warning) toast.info(session.warning);
      }
    },
    [setState, stateRef],
  );
  const begin = useCallback(
    (field: Field, focused = false) => {
      if (active.current?.field !== field) finish();
      if (!active.current) {
        active.current = {
          field,
          base: stateRef.current,
          token: "timing-" + generateRandomId(16),
          focused,
          cancelled: false,
          warning: null,
          ids: [...targetIds],
          established: false,
          lastInput: null,
        };
        const session = active.current;
        setState({
          update: (state) => {
            if (!owns(session, state)) return state;
            session.lastInput = state;
            session.established = true;
            return {
              ...state,
              timelineInputOwner: session.token,
              documentMutationOwner: session.token,
            };
          },
          commitToUndoStack: true,
        });
      } else if (focused) active.current.focused = true;
      return active.current;
    },
    [finish, setState, stateRef, targetIds],
  );
  const update = useCallback(
    (field: Field, num: number, commit: boolean) => {
      const session = begin(field);
      if (session.cancelled) {
        if (commit && !session.focused) finish(true);
        return;
      }
      if (!owns(session, stateRef.current)) {
        session.cancelled = true;
        session.warning = null;
        if (commit && !session.focused) finish();
        return;
      }
      const result = applyTimingInput(
        session.base,
        itemId,
        session.ids,
        field,
        num,
      );
      session.warning = !result.ok
        ? result.reason === "target-occupied"
          ? "같은 트랙의 다른 에셋과 겹치는 위치입니다. 빈 곳으로 옮기거나 트랙 마그네틱을 켜서 삽입해 주세요."
          : "해당 시간으로 변경할 수 없습니다. 트랙 잠금과 입력값을 확인해 주세요."
        : field === "duration" &&
            result.state.undoableState.items[itemId]?.durationInFrames !== num
          ? "원본 길이 또는 다른 에셋의 시작 위치까지 조정했습니다. 더 늘리려면 에셋을 옮기거나 트랙 마그네틱을 켜 주세요."
          : null;
      if (result.ok)
        setState({
          mutationOwner: session.token,
          update: (current) => {
            if (!owns(session, current)) return current;
            session.lastInput = current;
            session.established = true;
            return {
              ...current,
              documentMutationOwner: session.token,
              undoableState: result.state.undoableState,
              selectedItems: result.state.selectedItems,
              timelineTimeEditApplied: true,
            };
          },
          commitToUndoStack: false,
        });
      if (commit && !session.focused) finish();
    },
    [begin, finish, itemId, setState, stateRef, targetIds],
  );
  const cancel = useCallback(() => {
    const session = active.current;
    if (!session) return;
    session.cancelled = true;
    setState({
      mutationOwner: session.token,
      update: (current) =>
        owns(session, current)
          ? {
              ...current,
              undoableState: session.base.undoableState,
              selectedItems: session.base.selectedItems,
              timelineTimeEditApplied: true,
            }
          : current,
      commitToUndoStack: false,
    });
  }, [setState]);
  useEffect(() => () => finish(true, true), [finish, itemId]);
  const finishField = useCallback(
    (field: Field) => {
      if (active.current?.field === field) finish();
    },
    [finish],
  );
  return { begin, update, cancel, finish, finishField, revision };
}
